/**
 * Sliding-pill segmented control.
 *
 * Design intent:
 * - Geometry follows the widest label, so text is NEVER clipped (no ellipsis).
 * - Uses semantic theme tokens (`background.cardElevated`, `primary`, inverted gray scale)
 *   so the same component looks correct in light, normal and dark themes.
 * - Thumb animates via `translateX` + native driver; dimensions are measured,
 *   never hard-coded, so it stays a true capsule at any size.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  View,
  Text,
  Pressable,
  Animated,
  Easing,
  LayoutChangeEvent,
  Platform,
} from 'react-native'
import type { LucideIcon } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations'
import { useTheme } from '../../../src/theme/useTheme'
import { ThemeContext } from '../../../src/theme/ThemeContext'
import {
  getAnimatedSegmentedControlStyles,
  type AnimatedSegmentedControlVariant,
} from './AnimatedSegmentedControl.styles'

const ANIM_MS = 240

export interface AnimatedSegmentOption<T extends string = string> {
  key: T
  label: string
  icon?: LucideIcon
}

export interface AnimatedSegmentedControlProps<T extends string = string> {
  options: AnimatedSegmentOption<T>[]
  value: T
  onChange: (key: T) => void
  variant?: AnimatedSegmentedControlVariant
  fullWidth?: boolean
  testID?: string
}

export function AnimatedSegmentedControl<T extends string>({
  options,
  value,
  onChange,
  variant = 'default',
  fullWidth = false,
  testID,
}: AnimatedSegmentedControlProps<T>): React.ReactElement | null {
  const theme = useTheme()
  const themeCtx = useContext(ThemeContext)
  const themeMode = themeCtx?.themeMode ?? 'light'
  const count = options.length

  /** Intrinsic controls use the widest segment; full-width controls split the measured track evenly. */
  const [measuredSegmentWidth, setMeasuredSegmentWidth] = useState(0)
  const [trackWidth, setTrackWidth] = useState(0)
  const segmentWidth =
    fullWidth && trackWidth > 0 && count > 0 ? trackWidth / count : measuredSegmentWidth

  const styles = useMemo(
    () => getAnimatedSegmentedControlStyles(theme, variant, themeMode, segmentWidth, fullWidth),
    [theme, variant, themeMode, segmentWidth, fullWidth],
  )

  const activeIndex = useMemo(() => {
    const i = options.findIndex((o) => o.key === value)
    return i >= 0 ? i : 0
  }, [options, value])

  const optionsKey = useMemo(
    () => options.map((o) => o.key).join('|'),
    [options],
  )
  const widthByIndex = useRef<number[]>([]).current

  const handleMeasure = useCallback(
    (index: number) => (e: LayoutChangeEvent) => {
      const w = e.nativeEvent.layout.width
      if (!Number.isFinite(w) || w <= 0) return
      widthByIndex[index] = w
      const next = Math.max(...widthByIndex.filter((v) => v && v > 0))
      setMeasuredSegmentWidth((prev) => (next > prev ? next : prev))
    },
    [widthByIndex],
  )

  const handleTrackLayout = useCallback((e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width
    if (!Number.isFinite(w) || w <= 0) return
    setTrackWidth((prev) => (prev === w ? prev : w))
  }, [])

  useEffect(() => {
    widthByIndex.length = 0
    setMeasuredSegmentWidth(0)
  }, [optionsKey, widthByIndex])

  const thumbX = useRef(new Animated.Value(0)).current
  const primedOnce = useRef(false)

  useEffect(() => {
    if (segmentWidth <= 0) return
    const toValue = activeIndex * segmentWidth
    if (!primedOnce.current) {
      thumbX.setValue(toValue)
      primedOnce.current = true
      return
    }
    Animated.timing(thumbX, {
      toValue,
      duration: ANIM_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start()
  }, [activeIndex, segmentWidth, thumbX])

  useEffect(() => {
    primedOnce.current = false
  }, [optionsKey])

  if (count === 0) return null

  return (
    <View style={styles.outer} accessibilityRole="tablist" testID={testID}>
      <View style={styles.track} onLayout={handleTrackLayout}>
        {segmentWidth > 0 && (
          <Animated.View
            pointerEvents="none"
            style={[styles.thumb, { transform: [{ translateX: thumbX }] }]}
          />
        )}
        {options.map((opt, index) => {
          const isActive = activeIndex === index
          return (
            <Pressable
              key={String(opt.key)}
              onLayout={handleMeasure(index)}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              onPress={() => onChange(opt.key)}
              testID={testID ? `${testID}-${opt.key}` : undefined}
              style={({ pressed }) => [
                styles.segmentPressable,
                pressed && styles.segmentPressed,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              ]}
            >
              {opt.icon != null && (
                <opt.icon
                  size={13}
                  color={(isActive ? styles.segmentLabelActive : styles.segmentLabel).color as string}
                  strokeWidth={2.2}
                />
              )}
              <Text
                style={[
                  styles.segmentLabel,
                  isActive && styles.segmentLabelActive,
                ]}
              >
                {opt.label}
              </Text>
            </Pressable>
          )
        })}
      </View>
    </View>
  )
}
